import moment from "moment";
import * as t from "io-ts";
import { uniq } from "lodash";
import {
  AssetReferenceIO,
  AttachmentExternalReferenceT,
  NoteToAddT,
  RawTaskT,
  TodoNoteT,
  TodoNoteTagIO,
  TodoNoteTagT,
	UrlExternalReferenceIO,
	UrlExternalReferenceT
} from ".";
import { TaskEventKeys, TaskEventKeyT } from "./analytics";

export const CommentIO = t.type({
	isPinned:t.boolean,
  attachments: t.array(UrlExternalReferenceIO),
  date: t.number,
  standardTimestampFormat: t.string,
  text: t.union([t.string, t.undefined]),
  timeAgo: t.string,
  timestamp: t.union([t.number, t.undefined]),
  username: t.union([t.string, t.undefined]),
  tags: t.union([t.array(TodoNoteTagIO), t.undefined]),
  noteId: t.union([t.string, t.undefined]),
});
export type CommentT = t.TypeOf<typeof CommentIO>;
export const ALL_ACTIONABLE_STATES = {
  IN_PENDING_STATE: "PENDING",
  IN_INBOX_STATE: "INBOX",
  REJECTED_BY_PUBLISHER_TO_EDITOR: "REJECTED_BY_PUBLISHER_TO_EDITOR",
  IN_PROGRESS_STATE: "IN_PROGRESS",
  IN_APPROVED_STATE: "APPROVED",
  IN_REJECTED_STATE: "REJECTED",
  IN_COMPLETED_STATE: "COMPLETED",
  IN_PEER_REVIEW_STATE: "PEER_REVIEW",
  IN_BLOCKED_STATE: "BLOCKED",
} as const;

export const {
  IN_PENDING_STATE,
  IN_INBOX_STATE,
  REJECTED_BY_PUBLISHER_TO_EDITOR,
  IN_PROGRESS_STATE,
  IN_APPROVED_STATE,
  IN_REJECTED_STATE,
  IN_COMPLETED_STATE,
  IN_PEER_REVIEW_STATE,
  IN_BLOCKED_STATE,
} = ALL_ACTIONABLE_STATES;

const SELF_ASSIGNABLE_STATES = ["_UNASSIGNED_", "ESCALATED"];

export const makeTaskT = (rawTask: RawTaskT) => {
  const getNotes = (rawTask: RawTaskT): TodoNoteT[] => {
		const notes = rawTask.notes;
		if (!notes || !Array.isArray(notes)) return [];

		return notes.filter((note) => {
			const hasText = note.text.trim() !== '';

			const hasReferences = Array.isArray(note.references) && note.references.length > 0;
			return hasText || hasReferences;
		});
	};


  const getRefs = (notes: TodoNoteT[]) => {
    return notes.filter((note) => {
      return (
        note.references?.[0]?.reference_type === "URL" ||
        (note.references?.[0]?.reference_type === "ATTACHMENT" &&
          /^\d+$/.test(note.references?.[0]?.asset_reference?.id ?? ""))
      );
    });
  };


  const getLatestNote = (rawTask: RawTaskT) => {
    const notes = getNotes(rawTask);
    if (!notes?.length) {
      return undefined;
    }
    return notes[notes.length - 1];
  };

  const getAttachments = (rawTask: RawTaskT) => {
    const notesWithRefs = getRefs(getNotes(rawTask));
    const attachments: AttachmentExternalReferenceT[] = [];
    
    notesWithRefs.forEach(note => {
      note.references?.forEach(ref => {
        if (ref.reference_type === "ATTACHMENT") {
          attachments.push(ref as AttachmentExternalReferenceT);
        }
      });
    });

    return attachments;
  };
  const getSingleAttachment = (note:NoteToAddT) => {
    const attachments: UrlExternalReferenceT[] = [];
      note.references?.forEach(ref => {
        if (ref.reference_type === "URL") {
          attachments.push(ref as UrlExternalReferenceT);
        }
      });

    return attachments;
  };

  const getAttachment = (
    tags: TodoNoteTagT[],
    attachmentsDictionary: Record<string, any> | undefined
  ) => {
    const assetId =
      tags?.[0]?.key === "asset" && tags[0].value ? tags[0].value : undefined;

    return assetId && attachmentsDictionary
      ? attachmentsDictionary[assetId]
      : undefined;
  };

  const getAttachmentsDictionary = (rawTask: RawTaskT) => {
    const attachments = getAttachments(rawTask);
    const dictionary: Record<string, any> = {};

    for (const attachment of attachments) {
      if (!attachment.asset_reference.id) return;
      const { id, description, environment, name } = attachment.asset_reference;
      dictionary[id] = { id, description, environment, name };
    }

    return dictionary;
  };
  /* this function is used for merging seperate attachments into a
  single attachments array is they both are from same user and timestamp */

  const getMergedAttachments = ({
    notes,
    index,
    currentTimestamp,
    currentUsername,
    attachmentsDictionary,
  }: {
    notes: TodoNoteT[];
    index: number;
    currentTimestamp: number | undefined;
    currentUsername: string | undefined;
    attachmentsDictionary: Record<string, any> | undefined;
  }) => {
    const mergedAttachments = [];
    for (let index = 0; index < notes.length; index++) {
      const note = notes && notes[index];

      const { tags, user: username, timestamp } = note || {};
      const attachment = getAttachment(tags || [], attachmentsDictionary);

      const shouldMergeAttachment =
        username === currentUsername &&
        timestamp === currentTimestamp &&
        !!attachment;

      if (!shouldMergeAttachment) return { mergedAttachments, index };
      mergedAttachments.push(attachment ?? {});
    }

    /* retuns the merged attachments array as well as the the updated index number.
    We need to look for attachments post this index number now */

    return { mergedAttachments, index };
  };

  const getComments = (rawTask: RawTaskT) => {
    // const attachmentsDictionary = getAttachmentsDictionary(rawTask);
    const notes = getNotes(rawTask);

    const filteredNotes =
      notes?.filter((note) => {
        return note.tags?.[0]?.key;
      }) ?? [];
    //array used to store text and attachamnets in the same index if they were created together by the same user

    const comments: CommentT[] = [];
    let idx = 0;

    while (idx < filteredNotes.length) {
      const note = filteredNotes && filteredNotes[idx];
      const { text, user: username, timestamp, tags, noteId } = note ?? {};
      // const date = timestamp && new Date(timestamp * 1e3);
      const date = timestamp !== undefined ? timestamp : Date.now();
      const standardTimestampFormat = moment(date).format("lll");
      const timeAgo = moment(date).fromNow();

      // const attachment = getAttachment(tags || [], attachmentsDictionary);

      // const { mergedAttachments, index } = getMergedAttachments({
      //   notes: filteredNotes || [],
      //   index: attachment ? idx : idx + 1,
      //   currentTimestamp: timestamp,
      //   currentUsername: username,
      //   attachmentsDictionary,
      // });

      /* updating idx with index because we don't want to go over the same elements
      again that we have already traversed in the func getMergedAttachments */
			
      idx++;
	
      const res = {
        attachments:  getSingleAttachment(note),
        date,
        standardTimestampFormat,
        text: text,
        timeAgo,
        timestamp,
        username,
        tags: tags,
        noteId,
        isPinned: note.isPinned ?? false,
      };
      comments.push(res);
    }
    return comments;
  };

  const getUniqDiscussionParticipants = (rawTask: RawTaskT) => {
    const users = getNotes(rawTask)?.map((ref) => ref.user);
    return uniq(users);
  };

  return Object.freeze({
    get _raw() {
      return rawTask;
    },
    // get fsmId() {
    //   return rawTask.ticket_info.current_ticket_id;
    // },
    get companyID() {
      return rawTask.companyDetail?.companyID || rawTask?.companyID;
    },
    get companyName() {
      return rawTask.companyDetail?.name;
    },
    get assignee() {
      return rawTask.assignee;
    },
    get isPending() {
      return rawTask.current_state === IN_PENDING_STATE;
    },
    get isInInbox() {
      return rawTask.current_state === IN_INBOX_STATE || rawTask.current_state === REJECTED_BY_PUBLISHER_TO_EDITOR;
    },
    get isApproved() {
      return rawTask.current_state === IN_APPROVED_STATE;
    },
    get isRejectedByPublisher() {
      return rawTask.current_state === REJECTED_BY_PUBLISHER_TO_EDITOR;
    }, 
    get isRejected() {
      return rawTask.current_state === IN_REJECTED_STATE;
    },
    get isCompleted() {
      return rawTask.current_state === IN_COMPLETED_STATE;
    },
    get configuration() {
      return rawTask.configuration;
    },
    get isInPeerReview() {
      return rawTask.current_state === IN_PEER_REVIEW_STATE;
    },
    get isBlocked() {
      return rawTask.current_state === IN_BLOCKED_STATE;
    },
    get configName() {
      return rawTask?.configuration?.name;
    },
    get configData() {
      return rawTask.configuration;
    },
    get tags() {
      return rawTask?.configuration?.tags;
    },
    get configPriority() {
      return rawTask?.configuration?.priority;
    },
    get configScope() {
      return rawTask?.configuration?.scope;
    },
    get configScopeType() {
      return rawTask?.configuration?.scope_type;
    },
    get configScrapeStrategy() {
      return rawTask?.configuration?.scrape_strategy;
    },
    get configUuid() {
      return rawTask?.configuration?.uuid;
    },

    get discussionLength() {
      const taskRefsCount = getRefs(getNotes(rawTask))?.length;
      const taskNotesCount = getNotes(rawTask)?.length;
      if ((taskRefsCount ?? taskNotesCount) === undefined) {
        return undefined;
      }
      return (taskRefsCount ?? 0) + (taskNotesCount ?? 0);
    },
    get id() {
      return rawTask.task_id;
    },
    get isAssigned() {
      return !!rawTask.assignee;
    },
    get isFastlaneTask() {
      return rawTask.isFastlaneTask;
    },
    get taskPriority() {
      return rawTask.priority ?? this.configPriority();
    },
    get isInProgress() {
      return rawTask.current_state === IN_PROGRESS_STATE;
    },
    get isReadyForPublish() {
      return rawTask.current_state === IN_APPROVED_STATE;
    },
    get isReassignable() {
      return ([IN_PROGRESS_STATE] as unknown[]).includes(rawTask.current_state);
    },

    get creationTime() {
      return rawTask.created_at ? new Date(rawTask.created_at) : undefined;
    },
    get lastModifiedBy() {
      return rawTask.last_modified_by;
    },
    get lastModifiedTime() {
      return rawTask.last_modified_at
        ? new Date(rawTask.last_modified_at)
        : undefined;
    },

    get lastNoteTime() {
      const latestNote = getLatestNote(rawTask);
      return latestNote ? new Date((latestNote.timestamp ?? Date.now()) * 1e3) : undefined;
    },
    get latestNote() {
      return getLatestNote(rawTask);
    },
    get notes() {
      return getNotes(rawTask);
    },
    get refs() {
      return getRefs(getNotes(rawTask));
    },
    get scheduledTime() {
      return rawTask.scheduled_at ? new Date(rawTask.scheduled_at) : undefined;
    },
    get state() {
      return rawTask.state;
    },
    get currentState() {
      return rawTask.current_state;
    },
    get currentStateReadable() {
      return rawTask.current_state ? TaskEventKeys[rawTask.current_state as TaskEventKeyT]: rawTask.current_state;
    },
    get inCurrentStateTime() {
      const filteredState = rawTask.state
        ?.filter((s) => s.state === rawTask.current_state).pop();
      return filteredState?.updated_at ? Number(filteredState.updated_at) : Date.now();
    },
    get isSelfAssignable() {
      return (
        !!rawTask.current_state &&
        SELF_ASSIGNABLE_STATES.includes(rawTask.current_state)
      );
    },
    get title() {
      return rawTask?.info?.title;
    },
    get type() {
      return rawTask.type;
    },
    get attachmentsDictionary() {
      return getAttachmentsDictionary(rawTask);
    },
    get comments() {
      return getComments(rawTask);
    },
    get uniqDiscussionParticipants() {
      return getUniqDiscussionParticipants(rawTask);
    },
    get attachments() {
      return getAttachments(rawTask);
    },
    get info() {
      return rawTask.info;
    },
  });
};

export type TaskT = ReturnType<typeof makeTaskT>;

export const TaskAssignmentActions = {
  REASSIGN: null,
  UNASSIGN: null,
  SELF_ASSIGN: null,
};
export const TaskOtherActions = { EDIT: null };

export type TaskAssignmentActionTypeT = keyof typeof TaskAssignmentActions;
export type TaskOtherActionT = keyof typeof TaskOtherActions;

export type TaskActionTypeT = TaskAssignmentActionTypeT | TaskOtherActionT;

export type TaskActionT = {
  task: TaskT;
  actionType: TaskActionTypeT;
};

export type RowContextMenuActionT =
  | TaskActionTypeT
  | "SHOW_JSON"
  | "OPEN_CHILD";
